import { useNavigate, useParams, useLocation } from "react-router-dom";
import { YStack, XStack, View, Text } from "tamagui";
import { RiArrowLeftSLine } from "react-icons/ri";

import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";
import { IdentityContext } from "@utils/store/user/useUser";

import SectionLabel from "./components/ui/SectionLabel";
import ListContainer from "./components/ui/ListContainer";
import InfoNote from "./components/ui/InfoNote";

interface PriceItem {
  id: string;
  /** 規格 e.g. 16kg 桶裝瓦斯 */
  spec: string;
  price: number;
  /** 單位 e.g. 桶 / 次 */
  unit: string;
  note?: string;
}

/**
 * 定價表（依瓦斯行）。切版階段 items 傳空陣列、顯示空狀態；之後接 API 換成該瓦斯行的定價。
 */
export const Pricing = () => {
  const navigate = useNavigate();
  const { contextId } = useParams();
  const location = useLocation();
  const ctx = (location.state as { context?: IdentityContext } | null)?.context ?? null;
  const back = () => navigate(`/account/detail/${contextId}`, { state: { context: ctx } });

  const vendor = ctx?.primary_supplier?.supplier_name ?? "瓦斯行";

  // TODO(API): 取得此瓦斯行的桶裝瓦斯定價
  const items: PriceItem[] = [];

  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      <Header>
        <XStack ai="center" onPress={back} cursor="pointer" mb="$3">
          <View mr="$2">
            <RiArrowLeftSLine size={20} color={colors.light} />
          </View>
          <Text color={colors.light} fontWeight="bold" fontSize={16}>
            返回
          </Text>
        </XStack>
        <Text color={colors.light} fontSize={22} fontWeight="800" mb="$1.5">
          定價表
        </Text>
        <Text color="rgba(255,255,255,0.85)" fontSize={12}>
          {vendor}
        </Text>
      </Header>

      <RadiusContentWrapper space="$3.5">
        <SectionLabel title="桶裝瓦斯定價" />
        <ListContainer>
          {items.length === 0 ? (
            <Text fontSize={13} color={colors.hint} ta="center" py="$8">
              尚無定價資料
            </Text>
          ) : (
            items.map((item, i) => (
              <XStack key={item.id} ai="flex-start" jc="space-between" gap="$3" px="$4" py="$3.5" bbw={i === items.length - 1 ? 0 : 1} bbc={colors.borderLight}>
                <YStack f={1} miw={0}>
                  <Text fontSize={14} fontWeight="600" color={colors.text}>
                    {item.spec}
                  </Text>
                  {item.note && (
                    <Text fontSize={11} color={colors.hint} mt="$1">
                      {item.note}
                    </Text>
                  )}
                </YStack>
                <YStack ai="flex-end" flexShrink={0}>
                  <Text fontSize={17} fontWeight="800" color={colors.primary} style={{ fontVariantNumeric: "tabular-nums" }}>
                    <Text fontSize={11} fontWeight="600" color={colors.sub}>
                      ${" "}
                    </Text>
                    {item.price}
                  </Text>
                  <Text fontSize={10} color={colors.hint} mt="$1">
                    / {item.unit}
                  </Text>
                </YStack>
              </XStack>
            ))
          )}
        </ListContainer>

        <InfoNote tone="primary">
          本價格由 {vendor} 提供，實際金額以訂單為準；漲跌價時 App 會推播通知。
        </InfoNote>
      </RadiusContentWrapper>
    </YStack>
  );
};

export default Pricing;
